import { useEffect, useState, useCallback } from 'react';
import { claimInterruptionSlot } from './useInterruptionGate';

const DISMISSED_KEY = 've_contact_modal_dismissed';

// Never interrupt someone already on the lead-capture form itself, or
// already mid-application — firing "Start your journey" on top of the
// sign-up wizard undermines the exact thing it's trying to capture.
const EXCLUDED_PATH_PREFIXES = ['/sign-up', '/my-applications', '/admin-portal'];

/** P11 item 6 — replaces the old flat "fire 10 seconds after load"
 * ContactInfoModal trigger, which interrupted every visitor identically
 * regardless of engagement. Now gated behind a real intent signal: only
 * once the visitor has scrolled past 60% of the page AND spent 45+ seconds
 * on it, and only if they haven't dismissed the modal before (persisted
 * across sessions — a "no" is a "no"), and only if it wins the shared
 * per-session interruption slot (see useInterruptionGate). */
export default function useContactModalIntent() {
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('token')) return undefined; // staff/logged-in
    if (localStorage.getItem(DISMISSED_KEY)) return undefined;

    let scrollDepthOk = false;
    let timeOk = false;
    let settled = false;

    const tryShow = () => {
      if (settled || !scrollDepthOk || !timeOk) return;
      // Checked live (not just at mount) — a client-side nav into the
      // admin portal after this effect started shouldn't still let the
      // popup fire there.
      if (EXCLUDED_PATH_PREFIXES.some((p) => window.location.pathname.startsWith(p))) return;
      if (!claimInterruptionSlot()) { settled = true; cleanup(); return; }
      settled = true;
      setShouldShow(true);
      cleanup();
    };

    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const pct = max > 0 ? (window.scrollY / max) * 100 : 100;
      if (pct >= 60) { scrollDepthOk = true; tryShow(); }
    };

    const timer = setTimeout(() => { timeOk = true; tryShow(); }, 45000);
    window.addEventListener('scroll', onScroll, { passive: true });

    function cleanup() {
      clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
    }
    return cleanup;
  }, []);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISSED_KEY, '1');
    setShouldShow(false);
  }, []);

  return { shouldShow, dismiss };
}
